import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useLang } from "../lib/i18n";
import { FAMILIES, pick } from "../lib/industries";
import { SIZES, GOALS, FREQ, PICK_TXT, L, sizeLabel, goalLabel, freqLabel } from "../lib/businessLabels";

const ACTIVITIES = ["Restaurant / Café", "Commerce de détail", "Artisan", "Hôtel", "Salon de beauté", "Indépendant", "Autre"];

export default function Onboarding() {
  const nav = useNavigate();
  const { lang } = useLang();
  const Lg = (m) => L(m, lang);
  const [step, setStep] = useState(0);
  const [mSearch, setMSearch] = useState("");
  const [f, setF] = useState({
    business_name: "", country: "France", city: "", activity_family: "", activity_type: "", custom_activity: "", company_size: "", employees: "",
    facebook: false, instagram: false, google: false, website: false, loyalty_program: false, whatsapp: false, tiktok: false, email_marketing: false, sms_marketing: false,
    promo_frequency: "", events_frequency: "", posting_frequency: "", main_goal: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const steps = 4;
  const progress = ((step + 1) / steps) * 100;

  const next = () => step < steps - 1 ? setStep(step + 1) : nav("/analyzing", { state: { onboarding: f } });
  const back = () => step > 0 && setStep(step - 1);

  const SwitchRow = ({ k, label }) => (
    <div className="flex items-center justify-between py-3 border-b border-white/5">
      <Label className="text-sm">{label}</Label>
      <Switch data-testid={`switch-${k}`} checked={f[k]} onCheckedChange={(v) => set(k, v)} />
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden grain flex flex-col">
      <div className="absolute inset-0 aurora" />
      <div className="relative z-10 max-w-lg mx-auto w-full px-5 py-8 flex-1 flex flex-col">
        <div className="mb-6"><Logo /></div>
        <div className="mb-8">
          <div className="flex justify-between text-xs text-[hsl(var(--muted-foreground))] mb-2">
            <span>{lang === "en" ? `Step ${step + 1} of ${steps}` : lang === "es" ? `Paso ${step + 1} de ${steps}` : `Étape ${step + 1} sur ${steps}`}</span><span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <motion.div className="h-full bg-gradient-to-r from-primary to-neon" animate={{ width: `${progress}%` }} transition={{ ease: "easeOut" }} data-testid="onboarding-progress" />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="flex-1">
            {step === 0 && (
              <div className="space-y-5">
                <h2 className="font-display font-bold text-2xl">Parlez-nous de votre entreprise</h2>
                <div><Label className="text-xs">Nom de l'entreprise</Label><Input data-testid="ob-name" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Ex : NOVA COFFEE" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Pays</Label><Input data-testid="ob-country" value={f.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                  <div><Label className="text-xs">Ville</Label><Input data-testid="ob-city" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Lyon" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                </div>
                <div><Label className="text-xs">Famille d'activité</Label>
                  <Select value={f.activity_family} onValueChange={(v) => { set("activity_family", v); set("activity_type", ""); set("custom_activity", ""); setMSearch(""); }}>
                    <SelectTrigger data-testid="ob-family" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue placeholder={Lg(PICK_TXT.chooseFam)} /></SelectTrigger>
                    <SelectContent>{FAMILIES.map((fam) => <SelectItem key={fam.id} value={fam.id}>{pick(fam.label, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {f.activity_family && (() => {
                  const fam = FAMILIES.find((x) => x.id === f.activity_family);
                  const metiers = fam ? fam.metiers.filter(([, lbl]) => pick(lbl, lang).toLowerCase().includes(mSearch.toLowerCase())) : [];
                  return (
                    <div><Label className="text-xs">Métier précis</Label>
                      <Input data-testid="ob-metier-search" value={mSearch} onChange={(e) => setMSearch(e.target.value)} placeholder={Lg(PICK_TXT.searchActivity)} className="mt-1.5 mb-2 bg-white/5 border-white/10 h-10" />
                      <Select value={f.activity_type} onValueChange={(v) => set("activity_type", v)}>
                        <SelectTrigger data-testid="ob-metier" className="bg-white/5 border-white/10 h-11"><SelectValue placeholder={Lg(PICK_TXT.chooseTrade)} /></SelectTrigger>
                        <SelectContent>{metiers.map(([id, lbl]) => <SelectItem key={id} value={pick(lbl, lang)}>{pick(lbl, lang)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  );
                })()}
                {f.activity_family === "autre" && (
                  <div><Label className="text-xs">Décrivez votre activité</Label><Input data-testid="ob-custom-activity" value={f.custom_activity} onChange={(e) => set("custom_activity", e.target.value)} placeholder="Ex : atelier de céramique" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                )}
              </div>
            )}
            {step === 1 && (
              <div className="space-y-5">
                <h2 className="font-display font-bold text-2xl">Votre structure</h2>
                <div><Label className="text-xs">Taille de l'entreprise</Label>
                  <Select value={f.company_size} onValueChange={(v) => set("company_size", v)}>
                    <SelectTrigger data-testid="ob-size" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue placeholder={Lg(PICK_TXT.choose)} /></SelectTrigger>
                    <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{sizeLabel(s, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Nombre de salariés</Label><Input data-testid="ob-employees" type="number" value={f.employees} onChange={(e) => set("employees", e.target.value)} placeholder="4" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                <div><Label className="text-xs">Objectif commercial principal</Label>
                  <Select value={f.main_goal} onValueChange={(v) => set("main_goal", v)}>
                    <SelectTrigger data-testid="ob-goal" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue placeholder={Lg(PICK_TXT.choose)} /></SelectTrigger>
                    <SelectContent>{GOALS.map((g) => <SelectItem key={g} value={g}>{goalLabel(g, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-2">
                <h2 className="font-display font-bold text-2xl mb-4">Votre présence digitale</h2>
                <SwitchRow k="facebook" label="Présence Facebook" />
                <SwitchRow k="instagram" label="Présence Instagram" />
                <SwitchRow k="google" label="Fiche Google Business" />
                <SwitchRow k="website" label="Site internet" />
                <SwitchRow k="whatsapp" label="WhatsApp Business" />
                <SwitchRow k="tiktok" label="TikTok" />
                <SwitchRow k="email_marketing" label="Email / Newsletter" />
                <SwitchRow k="sms_marketing" label="SMS marketing" />
                <SwitchRow k="loyalty_program" label="Programme de fidélité" />
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5">
                <h2 className="font-display font-bold text-2xl">Votre rythme commercial</h2>
                {[["promo_frequency", "Fréquence des promotions"], ["events_frequency", "Fréquence des animations"], ["posting_frequency", "Fréquence des publications"]].map(([k, l]) => (
                  <div key={k}><Label className="text-xs">{l}</Label>
                    <Select value={f[k]} onValueChange={(v) => set(k, v)}>
                      <SelectTrigger data-testid={`ob-${k}`} className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue placeholder={Lg(PICK_TXT.choose)} /></SelectTrigger>
                      <SelectContent>{FREQ.map(([v]) => <SelectItem key={v} value={v}>{freqLabel(v, lang)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}

                <div className="pt-4 mt-2 border-t border-white/10">
                  <h3 className="font-display font-bold text-base mb-1">📊 Vos indicateurs commerciaux — Facultatif</h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Renseignez ce que vous connaissez. Ces chiffres affinent le diagnostic et les recommandations. Vous pouvez tout laisser vide.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Chiffre d'affaires mensuel</Label><Input data-testid="ob-revenue_monthly" type="number" value={f.revenue_monthly || ""} onChange={(e) => set("revenue_monthly", e.target.value)} placeholder="€ / mois" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                    <div><Label className="text-xs">Marge brute (%)</Label><Input data-testid="ob-gross_margin" type="number" value={f.gross_margin || ""} onChange={(e) => set("gross_margin", e.target.value)} placeholder="%" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                    <div><Label className="text-xs">Panier moyen</Label><Input data-testid="ob-avg_basket" type="number" value={f.avg_basket || ""} onChange={(e) => set("avg_basket", e.target.value)} placeholder="€" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                    <div><Label className="text-xs">Clients / tickets mensuels</Label><Input data-testid="ob-monthly_customers" type="number" value={f.monthly_customers || ""} onChange={(e) => set("monthly_customers", e.target.value)} placeholder="Nombre" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                    <div className="col-span-2"><Label className="text-xs">Évolution du chiffre d'affaires (%)</Label><Input data-testid="ob-revenue_trend" type="number" value={f.revenue_trend || ""} onChange={(e) => set("revenue_trend", e.target.value)} placeholder="% vs période précédente" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex gap-3 mt-8">
          {step > 0 && <Button data-testid="ob-back" variant="outline" onClick={back} className="rounded-full border-white/10 bg-white/5"><ArrowLeft size={16} /></Button>}
          <Button data-testid="ob-next" onClick={next} disabled={step === 0 && !f.business_name}
            className="flex-1 rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
            {step === steps - 1
              ? <>{lang === "en" ? "Start the analysis" : lang === "es" ? "Iniciar el análisis" : "Lancer l'analyse"} <Check size={16} className="ml-1" /></>
              : <>{lang === "en" ? "Continue" : lang === "es" ? "Continuar" : "Continuer"} <ArrowRight size={16} className="ml-1" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
